import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { contractsApi, type Contract } from "@/lib/api";
import { getAllSuppliersWithRatings, recommendationColors, getLatestReviewForSupplier } from "@/mocks/supplierReviews";
import SummaryCards from "./components/SummaryCards";
import FilterBar from "./components/FilterBar";
import ExpiryTable from "./components/ExpiryTable";
import WorkflowWidgets from "./components/WorkflowWidgets";

function getScoreColor(score: number): string {
  if (score >= 4.0) return "bg-secondary-500";
  if (score >= 2.5) return "bg-accent-500";
  return "bg-foreground-400";
}

function daysFromNow(dateStr: string): number {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [filteredContracts, setFilteredContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contractsApi.list(1)
      .then((res) => {
        setContracts(res.data.data);
        setFilteredContracts(res.data.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const activeContracts = useMemo(
    () => filteredContracts.filter((c) => c.status !== "expired" && c.status !== "terminated"),
    [filteredContracts]
  );

  const suppliersWithRatings = useMemo(() => getAllSuppliersWithRatings(), []);

  const suppliersAtRisk = useMemo(
    () =>
      suppliersWithRatings
        .filter((s) => s.latestReview.recommendation === "Re-Advertise" || s.latestReview.recommendation === "Conditional Renewal")
        .sort((a, b) => a.latestReview.overallScore - b.latestReview.overallScore)
        .slice(0, 5),
    [suppliersWithRatings]
  );

  const reAdvertiseAlerts = useMemo(() => {
    return contracts
      .filter((contract) => {
        if (contract.status === "expired" || contract.status === "terminated") return false;
        const daysLeft = daysFromNow(contract.end_date);
        if (daysLeft < 0 || daysLeft > 180) return false;
        const review = getLatestReviewForSupplier(contract.vendor_name);
        return review?.recommendation === "Re-Advertise";
      })
      .sort((a, b) => daysFromNow(a.end_date) - daysFromNow(b.end_date));
  }, [contracts]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-primary-600 text-xl md:text-2xl font-bold">Dashboard</h1>
        <p className="text-foreground-500 text-sm mt-1">Contract expiry overview across all departments</p>
      </div>

      <SummaryCards contracts={activeContracts} />

      <WorkflowWidgets />

      {reAdvertiseAlerts.length > 0 && (
        <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-foreground-200 flex items-center justify-center">
                <i className="ri-close-circle-line text-foreground-500 text-sm"></i>
              </div>
              <div>
                <h2 className="text-primary-600 text-sm font-semibold">Re-Advertise Required</h2>
                <p className="text-foreground-400 text-xs">Supplier rated "Re-Advertise" in latest quarterly review — contract expiring soon</p>
              </div>
            </div>
            <span className="text-foreground-400 text-xs font-medium">
              {reAdvertiseAlerts.length} contract{reAdvertiseAlerts.length !== 1 ? "s" : ""} flagged
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200 bg-background-100">
                  {["Supplier", "Contract", "Department", "End Date", "Days Left", "Rating", "Recommendation", ""].map((h) => (
                    <th key={h} className={`text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap ${h === "" || h === "Days Left" || h === "Rating" || h === "Recommendation" ? "text-center" : h === "End Date" ? "text-right" : "text-left"}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reAdvertiseAlerts.map((contract) => {
                  const review = getLatestReviewForSupplier(contract.vendor_name);
                  const daysLeft = daysFromNow(contract.end_date);
                  const rec = recommendationColors["Re-Advertise"];
                  return (
                    <tr key={contract.id} className="border-b border-background-100 hover:bg-background-100 transition-colors">
                      <td className="px-4 py-3"><p className="text-foreground-800 text-sm font-medium">{contract.vendor_name}</p></td>
                      <td className="px-4 py-3"><span className="text-foreground-600 text-sm">{contract.contract_number}</span></td>
                      <td className="px-4 py-3"><span className="text-foreground-600 text-sm">{contract.department}</span></td>
                      <td className="px-4 py-3 text-right"><span className="text-foreground-600 text-sm">{formatDate(contract.end_date)}</span></td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[44px] h-6 rounded-full text-xs font-semibold ${daysLeft <= 30 ? "bg-foreground-100 text-foreground-700" : daysLeft <= 60 ? "bg-accent-50 text-accent-700" : "bg-primary-50 text-primary-700"}`}>
                          {daysLeft}d
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {review && (
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold text-white ${getScoreColor(review.overallScore)}`}>
                            {review.overallScore}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${rec.bg} ${rec.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${rec.dot}`}></span>
                          Re-Advertise
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => navigate(`/contracts/${contract.id}`)} className="h-7 px-3 bg-primary-600 text-white text-xs font-medium rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                            View Contract
                          </button>
                          <button onClick={() => navigate(`/supplier-reviews/${encodeURIComponent(contract.vendor_name)}`)} className="h-7 px-3 border border-background-300 text-foreground-600 text-xs font-medium rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                            Review History
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {suppliersAtRisk.length > 0 && (
        <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center">
                <i className="ri-alert-line text-accent-500 text-sm"></i>
              </div>
              <div>
                <h2 className="text-primary-600 text-sm font-semibold">Suppliers Needing Attention</h2>
                <p className="text-foreground-400 text-xs">Based on latest quarterly reviews</p>
              </div>
            </div>
            <button onClick={() => navigate("/supplier-reviews")} className="text-primary-600 text-xs font-medium hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
              View all reviews
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {suppliersAtRisk.map((item) => {
              const rec = recommendationColors[item.latestReview.recommendation];
              return (
                <div
                  key={item.supplier}
                  onClick={() => navigate(`/supplier-reviews/${encodeURIComponent(item.supplier)}`)}
                  className="bg-background-50 rounded-lg border border-background-200 p-3 hover:border-background-300 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-foreground-800 text-xs font-medium truncate flex-1 mr-2">{item.supplier}</p>
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold text-white shrink-0 ${getScoreColor(item.latestReview.overallScore)}`}>
                      {item.latestReview.overallScore}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mb-1.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-[10px] ${i < Math.floor(item.latestReview.overallScore) ? "ri-star-fill text-accent-500" : item.latestReview.overallScore - i >= 0.5 ? "ri-star-half-line text-accent-500" : "ri-star-line text-foreground-300"}`}></i>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${rec.bg} ${rec.text}`}>
                      <span className={`w-1 h-1 rounded-full ${rec.dot}`}></span>
                      {item.latestReview.recommendation === "Conditional Renewal" ? "Conditional" : item.latestReview.recommendation}
                    </span>
                    <span className="text-foreground-400 text-[10px]">{item.latestReview.quarter}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-primary-600 text-base font-semibold">Contracts by Expiry Date</h2>
          <span className="text-foreground-400 text-xs">
            {loading ? "Loading..." : `${filteredContracts.length} contract${filteredContracts.length !== 1 ? "s" : ""} found`}
          </span>
        </div>
        <FilterBar contracts={contracts} onFilter={setFilteredContracts} />
        {loading ? (
          <div className="py-10 text-center text-foreground-400 text-sm">
            <i className="ri-loader-4-line animate-spin mr-2"></i>Loading contracts...
          </div>
        ) : (
          <ExpiryTable contracts={filteredContracts} />
        )}
      </div>
    </div>
  );
}
